import { FC, useEffect, useMemo } from 'react'
import { Button, Col, Form, InputNumber, Row, message, notification } from 'antd'
import { useForm, useWatch } from 'antd/es/form/Form'
import { skipToken } from '@reduxjs/toolkit/query'
import MaterialTypesGrouped from '../../../../../../../components/form-items/material-types-grouped/material-types-grouped'
import AutoComplete from '../../../../../../../components/custom-form-controls/auto-complete'
import {
    requiredInputNumberRules,
    requiredOneElementRules,
} from '../../../../../../../constants/forms/validation/rules'
import { useCreateOrderRequestMutation } from '../../../../../../../store/api/order-requests-api'
import { useCreateMaterialMutation } from '../../../../../../../store/api/materials-api'
import { useGetMaterialTypesQuery } from '../../../../../../../store/api/material-types-api'
import { useGetStocksQuery } from '../../../../../../../store/api/stocks-api'
import useMaterialWidthOptions from '../../../../../../../helpers/render/use-material-width-options'
import calculateStockAmount from '../../../../../../../helpers/entities/stock/calculate-stock-amount'
import { orderRequestType } from '../../../../../../../constants/entity-enums/order-request-types'
import { useUser } from '../../../../../../../hooks/use-user'
import useErrorModal from '../../../../../../../hooks/api/use-error-modal'
import { resolveMaterialDocumentId } from '../../helpers/resolve-material-document-id'
import { createValidateMaterialWidth } from '../../helpers/parse-material-width-value'
import MaterialType from '../../../../../../../types/api/material-type'

interface CreateOrderRequestFormProps {
    onSuccess: () => void
}

interface CreateOrderRequestFormValues {
    materialTypeDocId: string
    material: string
    amount: number
}


const CreateOrderRequestForm: FC<CreateOrderRequestFormProps> = ({ onSuccess }) => {
    const [form] = useForm<CreateOrderRequestFormValues>()
    const user = useUser()
    const materialTypeDocId = useWatch('materialTypeDocId', form)

    const { data: stocksData } = useGetStocksQuery(
        materialTypeDocId
            ? {
                  filters: {
                      material: {
                          material_type: { documentId: { $eq: materialTypeDocId } },
                      },
                  },
                  populate: '*',
                  pagination: { page: 1, pageSize: 1000 },
              }
            : skipToken
    )
    const { data: materialTypeData } = useGetMaterialTypesQuery(
        materialTypeDocId
            ? {
                  filters: { documentId: { $eq: materialTypeDocId } },
                  populate: { unit: true },
              }
            : skipToken
    )
    const [createMaterial, createMaterialState] = useCreateMaterialMutation()
    const [createOrderRequest, createOrderRequestState] = useCreateOrderRequestMutation()

    useErrorModal(createMaterialState, 'Ошибка создания материала')
    useErrorModal(createOrderRequestState, 'Ошибка создания заявки')

    const materialOptions = useMaterialWidthOptions({
        items: stocksData?.data,
        getWidth: item => item.material.width,
        getDocumentId: item => item.material.documentId,
        getStock: item => calculateStockAmount(item),
    })

    const materials = useMemo(
        () => stocksData?.data.map(item => item.material) || [],
        [stocksData]
    )

    const validateMaterialWidth = useMemo(
        () => createValidateMaterialWidth(materialOptions, materials),
        [materialOptions, materials]
    )

    const selectedMaterialType = materialTypeData?.data[0] as MaterialType | undefined
    const unitShortName = selectedMaterialType?.unit?.shortName

    useEffect(() => {
        form.setFieldValue('material', undefined)
    }, [materialTypeDocId, form])

    const onSave = async () => {
        try {
            const values = await form.validateFields()

            if (!user?.documentId) {
                notification.error({ message: 'Не удалось определить инициатора заявки' })
                return
            }

            const materialDocumentId = await resolveMaterialDocumentId({
                materialTypeDocId: values.materialTypeDocId,
                materialValue: values.material,
                createMaterial,
                materials,
                materialOptions,
            })

            if (!materialDocumentId) {
                return
            }

            const unitDocumentId = selectedMaterialType?.unit?.documentId

            if (!unitDocumentId) {
                notification.error({ message: 'Не удалось определить единицу измерения материала' })
                return
            }

            const result = await createOrderRequest({
                data: {
                    goodsType: orderRequestType[0],
                    requestStatus: 'Новый',
                    amount: values.amount,
                    initiator: { connect: [user.documentId] },
                    material: { connect: [materialDocumentId] },
                    unit: { connect: [unitDocumentId] },
                },
            })

            if (result.data) {
                notification.success({ message: 'Заявка создана' })
                form.resetFields()
                onSuccess()
            }
        } catch (error) {
            message.error('Ошибка создания заявки')
            console.error(error)
        }
    }

    return (
        <Form form={form} layout={'vertical'}>
            <Row gutter={12}>
                <Col sm={8}>
                    <MaterialTypesGrouped
                        name={'materialTypeDocId'}
                        rules={requiredOneElementRules}
                        label={'Тип материала'}
                    />
                </Col>
                <Col sm={8}>
                    <Form.Item
                        name={'material'}
                        label={'Ширина'}
                        rules={[...requiredOneElementRules, { validator: validateMaterialWidth }]}
                    >
                        <AutoComplete
                            options={materialOptions}
                            placeholder='Выберите или введите ширину'
                            disabled={!materialTypeDocId}
                            allowClear
                            style={{ width: '100%' }}
                            dropdownStyle={{ minWidth: '250px' }}
                            getOptionKey={opt => opt.key}
                        />
                    </Form.Item>
                </Col>
                <Col sm={8}>
                    <Form.Item
                        name={'amount'}
                        label={unitShortName ? `Кол-во, ${unitShortName}` : 'Кол-во'}
                        rules={requiredInputNumberRules}
                    >
                        <InputNumber min={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>
            <Button
                type={'primary'}
                onClick={onSave}
                loading={createMaterialState.isLoading || createOrderRequestState.isLoading}
            >
                Создать заявку
            </Button>
        </Form>
    )
}

export default CreateOrderRequestForm
